/**
 * FeatureFlagsContext - Feature flag management for gradual rollout
 * 
 * Responsibilities:
 * - Managing feature flags from Supabase
 * - Local storage persistence
 * - Dynamic feature enabling/disabling
 * - Feature availability checking
 * 
 * Controlled Features:
 * - shopEnabled: Cosmetics shop interface
 * - groupsEnabled: Study groups functionality
 * - premiumUpsellEnabled: Premium upgrade prompts
 * - customAvatarsEnabled: Avatar customization
 * - achievementSystemEnabled: Achievements system
 * - customTimerUnlockEnabled: Custom timer durations
 * - badgesEnabled: Badge system
 * - collectionEnabled: Cosmetics collection
 * 
 * @example
 * ```tsx
 * const { flags, isFeatureEnabled } = useFeatureFlags();
 * 
 * if (isFeatureEnabled('shopEnabled')) {
 *   return <ShopInterface />;
 * }
 * 
 * return <ComingSoonCard feature="Shop" />;
 * ```
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSupabaseFeatureFlags } from '@/hooks/useFeatureFlags';

// Feature flags for future functionality
export interface FeatureFlags {
  shopEnabled: boolean;
  groupsEnabled: boolean;
  premiumUpsellEnabled: boolean;
  customAvatarsEnabled: boolean;
  achievementSystemEnabled: boolean;
  customTimerUnlockEnabled: boolean;
  badgesEnabled: boolean;
  collectionEnabled: boolean;
  tieredPurchasesEnabled: boolean; // Hide $1.99 one-time purchases at launch
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  updateFlag: (flag: keyof FeatureFlags, value: boolean) => void;
  isFeatureEnabled: (flag: keyof FeatureFlags) => boolean;
}

// Default flags - all false for MVP
const defaultFlags: FeatureFlags = {
  shopEnabled: false,
  groupsEnabled: false, 
  premiumUpsellEnabled: true, // Only this is enabled for MVP
  customAvatarsEnabled: false,
  achievementSystemEnabled: false, // New flag for achievements
  customTimerUnlockEnabled: true,
  badgesEnabled: true,
  collectionEnabled: false,
  tieredPurchasesEnabled: false, // Keep subscriptions simple at launch
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { flags: supabaseFlags, loading } = useSupabaseFeatureFlags();
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const saved = localStorage.getItem('studyforge-feature-flags');
    const parsed = saved ? JSON.parse(saved) : {};
    
    // Force badge visibility if it was cached as false
    if (parsed.badgesEnabled === false) {
      parsed.badgesEnabled = true;
      localStorage.setItem('studyforge-feature-flags', JSON.stringify(parsed));
    }
    
    return { ...defaultFlags, ...parsed };
  });

  // Merge Supabase flags with local flags when loaded
  useEffect(() => {
    if (!loading && Object.keys(supabaseFlags).length > 0) {
      setFlags(prev => ({
        ...prev,
        shopEnabled: supabaseFlags.shopEnabled ?? prev.shopEnabled,
        groupsEnabled: supabaseFlags.groupsEnabled ?? prev.groupsEnabled,
        premiumUpsellEnabled: supabaseFlags.premiumUpsellEnabled ?? prev.premiumUpsellEnabled,
        customAvatarsEnabled: supabaseFlags.customAvatarsEnabled ?? prev.customAvatarsEnabled,
        achievementSystemEnabled: supabaseFlags.achievementSystemEnabled ?? prev.achievementSystemEnabled,
        customTimerUnlockEnabled: supabaseFlags.customTimerUnlockEnabled ?? prev.customTimerUnlockEnabled,
        badgesEnabled: supabaseFlags.badgesEnabled ?? prev.badgesEnabled,
        collectionEnabled: supabaseFlags.collectionEnabled ?? prev.collectionEnabled,
        tieredPurchasesEnabled: supabaseFlags.tieredPurchasesEnabled ?? prev.tieredPurchasesEnabled,
      }));
    }
  }, [supabaseFlags, loading]);

  // Save to localStorage whenever flags change
  useEffect(() => {
    localStorage.setItem('studyforge-feature-flags', JSON.stringify(flags));
  }, [flags]);

  const updateFlag = (flag: keyof FeatureFlags, value: boolean) => {
    setFlags(prev => ({ ...prev, [flag]: value }));
  };

  const isFeatureEnabled = (flag: keyof FeatureFlags) => {
    return flags[flag];
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, updateFlag, isFeatureEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};